import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BarChart3, Download, ShoppingCart, Package, RefreshCcw, DollarSign, Activity, FileText } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const SupplierReports = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('Sales');
  
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState({
    sales: 0,
    orders: 0,
    products: 0,
    returns: 0,
    inventory: 0,
  });

  useEffect(() => {
    const fetchMetrics = async () => {
      if (!user?._id) return;
      setLoading(true);
      try {
        // Fetch products
        const productsRes = await axios.get(`${process.env.REACT_APP_API_URL}/products/get-all-products?supplier=${user._id}`);
        const products = productsRes.data.products || [];
        
        // Fetch orders
        const ordersRes = await axios.get(`${process.env.REACT_APP_API_URL}/orders/get-supplier-orders/${user._id}`);
        const orders = ordersRes.data || [];

        const totalSales = orders.reduce((sum, order) => sum + (order.totalAmount || 0), 0);
        const totalReturns = orders.filter(o => o.exchangeRequest?.isRequested).length;
        const totalInventory = products.reduce((sum, prod) => sum + (prod.stock || 0), 0);
        
        // Calculate Commission, Settlement, Performance
        const commissionRate = user?.commission || 20; // Default to 20% if missing
        const commissionAmount = (totalSales * commissionRate) / 100;
        const settlementAmount = totalSales - commissionAmount;
        
        const deliveredOrders = orders.filter(o => o.status === "Delivered").length;
        const performanceRate = orders.length > 0 ? ((deliveredOrders / orders.length) * 100).toFixed(1) : 100;

        setMetrics({
          sales: totalSales,
          orders: orders.length,
          products: products.length,
          returns: totalReturns,
          inventory: totalInventory,
          commission: commissionAmount,
          settlement: settlementAmount,
          performance: performanceRate,
        });
      } catch (err) {
        console.error("Error fetching report data", err);
      } finally {
        setLoading(false);
      }
    };
    fetchMetrics();
  }, [user]);

  const tabs = [
    { name: 'Sales', icon: DollarSign, value: `₹${metrics.sales.toFixed(2)}`, desc: "Total revenue from all orders" },
    { name: 'Orders', icon: ShoppingCart, value: metrics.orders, desc: "Total number of orders received" },
    { name: 'Products', icon: Package, value: metrics.products, desc: "Total active products in catalog" },
    { name: 'Inventory', icon: Package, value: metrics.inventory, desc: "Total physical stock across all products" },
    { name: 'Returns', icon: RefreshCcw, value: metrics.returns, desc: "Total return or exchange requests" },
    { name: 'Commission', icon: DollarSign, value: `₹${(metrics.commission || 0).toFixed(2)}`, desc: "Platform commission deduction" },
    { name: 'Settlement', icon: FileText, value: `₹${(metrics.settlement || 0).toFixed(2)}`, desc: "Net settlement to your bank" },
    { name: 'Performance', icon: Activity, value: `${metrics.performance || 0}%`, desc: "Order fulfillment success rate" },
  ];

  const activeTabData = tabs.find(t => t.name === activeTab);

  return (
    <section className="min-h-screen p-6 bg-[#EEF3F7]">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-xl md:text-3xl font-philosopher text-primaryTextColor flex items-center gap-2">
          <BarChart3 className="text-[#fba93d] w-7 h-7" />
          Report Analysis
        </h1>
        <button disabled className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-500 rounded-lg cursor-not-allowed">
          <Download size={18} />
          Download Report
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-sm mb-6 overflow-hidden">
        <div className="flex overflow-x-auto border-b border-gray-200 custom-scrollbar">
          {tabs.map((tab) => (
            <button
              key={tab.name}
              onClick={() => setActiveTab(tab.name)}
              className={`flex items-center gap-2 px-6 py-4 whitespace-nowrap font-medium text-sm transition-colors ${
                activeTab === tab.name
                  ? 'border-b-2 border-[#ea8d2a] text-[#ea8d2a]'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.name}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-10 flex flex-col items-center justify-center text-gray-500 min-h-[400px]">
        {loading ? (
          <div className="animate-pulse flex flex-col items-center">
            <div className="w-16 h-16 bg-gray-200 rounded-full mb-4"></div>
            <div className="h-6 bg-gray-200 rounded w-32 mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-48"></div>
          </div>
        ) : (
          <>
            {activeTabData?.icon && <activeTabData.icon className="w-16 h-16 text-[#ea8d2a] mb-4" />}
            <h2 className="text-3xl font-bold text-gray-800 mb-2">{activeTabData?.value}</h2>
            <h3 className="text-xl font-semibold text-gray-600 mb-2">{activeTab}</h3>
            <p className="mt-2 text-center max-w-md text-gray-500">
              {activeTabData?.desc}
            </p>
          </>
        )}
      </div>
    </section>
  );
};

export default SupplierReports;
